import { useState, useMemo, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';

import { Section, SectionCard, SectionHeader } from '@/components/common';
import { RichTextEditor } from '@/components/common/RichTextEditor/RichTextEditor';
import {
  useUpdateTcRunNotesForCollator,
  useApproveForDelivery,
  useReturnToCollator,
} from '@/queries/useTcRun';
import type { TcRunAppraiseeResponse } from '@/api/types/tcRun.types';
import { formatDateTime } from '@/utils/dateFormatter';
import { useTcRunAppraiseeMutationQueue } from './TcRunAppraiseeMutationQueueContext';

interface NotesForCollatorSectionProps {
  tcId: number;
  appraisee: TcRunAppraiseeResponse;
  readOnly: boolean;
}

export function NotesForCollatorSection({ tcId, appraisee, readOnly }: NotesForCollatorSectionProps) {
  const { runSerialized } = useTcRunAppraiseeMutationQueue();
  const updateMutation = useUpdateTcRunNotesForCollator();
  const approveMutation = useApproveForDelivery();
  const returnMutation = useReturnToCollator();

  const [localNotes, setLocalNotes] = useState<string>(appraisee.notesForCollator ?? '');
  const [localFsr, setLocalFsr] = useState(appraisee.isFinalStaffingReview);
  const [localAwaitingHr, setLocalAwaitingHr] = useState(appraisee.isAwaitingHrGuidance);
  const [validationAlert, setValidationAlert] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);

  // serverKey/prevKey pattern for cache-aware resets
  const serverKey = useMemo(
    () =>
      JSON.stringify({
        notes: appraisee.notesForCollator,
        fsr: appraisee.isFinalStaffingReview,
        awaiting: appraisee.isAwaitingHrGuidance,
      }),
    [appraisee.notesForCollator, appraisee.isFinalStaffingReview, appraisee.isAwaitingHrGuidance]
  );

  const [prevKey, setPrevKey] = useState(serverKey);
  if (serverKey !== prevKey) {
    setPrevKey(serverKey);
    setLocalNotes(appraisee.notesForCollator ?? '');
    setLocalFsr(appraisee.isFinalStaffingReview);
    setLocalAwaitingHr(appraisee.isAwaitingHrGuidance);
  }

  const isApproved = !!appraisee.approvedForDeliveryAt;
  const canToggleFsr = !!appraisee.finalRating;
  const canApprove = localFsr && !localAwaitingHr && !isApproved;

  // Records the last notes payload we shipped; prevents duplicate saves in the short
  // window between a save resolving and the `appraisee` prop re-rendering with the
  // new server state (during which `localNotes !== appraisee.notesForCollator` would
  // otherwise still look "dirty").
  const lastSavedNotesRef = useRef<string | null>(null);

  const isNotesDirty = () => {
    const current = localNotes || '';
    if (current === (appraisee.notesForCollator || '')) return false;
    if (lastSavedNotesRef.current !== null && current === lastSavedNotesRef.current) return false;
    return true;
  };

  // Single save path for the whole section — notes + both toggles in one atomic
  // backend call. `override` lets a toggle handler ship its new value before the
  // React state update has committed; without `override` it's a blur-save, which
  // short-circuits when the editor isn't dirty.
  const saveSection = async (override?: { fsr?: boolean; hr?: boolean }): Promise<void> => {
    const isBlurSave = override === undefined;
    if (isBlurSave && !isNotesDirty()) return;

    const notesToSave = localNotes || null;
    await runSerialized((rowVersion) =>
      updateMutation
        .mutateAsync({
          tcId,
          appraisalId: appraisee.appraisalId,
          data: {
            notesForCollator: notesToSave,
            isFinalStaffingReview: override?.fsr ?? localFsr,
            isAwaitingHrGuidance: override?.hr ?? localAwaitingHr,
          },
          rowVersion,
        })
        .then(() => {
          lastSavedNotesRef.current = notesToSave ?? '';
        })
    );
  };

  const handleNotesBlur = () => {
    void saveSection().catch(() => {
      /* 409 surfaced by the global Axios interceptor */
    });
  };

  const handleFsrToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    if (checked && !appraisee.finalRating) {
      setValidationAlert('Please set a Final Rating before enabling Final Staffing Review.');
      return;
    }
    setValidationAlert(null);
    setLocalFsr(checked);
    try {
      await saveSection({ fsr: checked });
    } catch {
      /* 409 surfaced globally */
    }
  };

  const handleAwaitingHrToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setLocalAwaitingHr(checked);
    try {
      await saveSection({ hr: checked });
    } catch {
      /* 409 surfaced globally */
    }
  };

  // Approve / Return are separate lifecycle actions (no notes payload). Flush any
  // dirty notes first via the queue so they don't get lost.
  const handleApproveConfirm = async () => {
    setApproveDialogOpen(false);
    try {
      if (isNotesDirty()) await saveSection();
      await runSerialized((rowVersion) =>
        approveMutation.mutateAsync({ tcId, appraisalId: appraisee.appraisalId, rowVersion })
      );
    } catch {
      /* 409 surfaced globally */
    }
  };

  const handleReturnConfirm = async () => {
    setReturnDialogOpen(false);
    try {
      if (isNotesDirty()) await saveSection();
      await runSerialized((rowVersion) =>
        returnMutation.mutateAsync({ tcId, appraisalId: appraisee.appraisalId, rowVersion })
      );
    } catch {
      /* 409 surfaced globally */
    }
  };

  const approvedTooltip = isApproved
    ? `Approved for Delivery -- ${formatDateTime(appraisee.approvedForDeliveryAt!)}`
    : '';

  return (
    <Section>
      <SectionHeader>NOTES &amp; ACTIONS FOR COLLATOR</SectionHeader>
      <SectionCard>
        {validationAlert && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setValidationAlert(null)}>
            {validationAlert}
          </Alert>
        )}

        <RichTextEditor
          content={localNotes}
          onChange={setLocalNotes}
          onBlur={handleNotesBlur}
          placeholder="Notes for collator..."
          disabled={readOnly}
          aria-label="Notes for Collator"
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mt: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={localFsr}
                onChange={handleFsrToggle}
                disabled={readOnly || !canToggleFsr}
                inputProps={{ 'aria-label': 'Final Staffing Review' }}
              />
            }
            label="Final Staffing Review"
          />

          <FormControlLabel
            control={
              <Switch
                checked={localAwaitingHr}
                onChange={handleAwaitingHrToggle}
                disabled={readOnly}
                inputProps={{ 'aria-label': 'Awaiting HR Guidance' }}
              />
            }
            label="Awaiting HR Guidance"
          />

          <Tooltip title={approvedTooltip} arrow>
            <span>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setApproveDialogOpen(true)}
                disabled={readOnly || !canApprove || isApproved}
              >
                Approve for Delivery
              </Button>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => setReturnDialogOpen(true)}
            disabled={readOnly || appraisee.status !== 'TcReview'}
          >
            Return to Collator
          </Button>
        </Box>

        {isApproved && (
          <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
            Approved for Delivery -- {formatDateTime(appraisee.approvedForDeliveryAt!)}
          </Typography>
        )}
      </SectionCard>

      {/* Approve confirmation dialog — wording and labels per Story 386 AC6.
          The action is irreversible (Approve button is disabled after approval),
          so the copy explicitly warns the user. */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)}>
        <DialogTitle>Approve for Delivery</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action will finalise the Staffing Review and move the appraisal form to
            &apos;Staffing Accepted&apos; status. You will not be able to make further changes
            after approval.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApproveConfirm} variant="contained" color="secondary">
            Approve for Delivery
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={returnDialogOpen} onClose={() => setReturnDialogOpen(false)}>
        <DialogTitle>Return to Collator</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to return this appraisal to the collator? This will send a notification email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReturnConfirm} variant="contained" color="secondary">
            Return
          </Button>
        </DialogActions>
      </Dialog>
    </Section>
  );
}
